import type { HunkerHost } from '../shared/hunker-contract.js';
import { inventoryFloorAvailable } from '../janus-engine/compile/inventory-facet.js';
import { statusCasterId } from '../janus-engine/statuses/expiry.js';
import { encounterCreatureField } from '../shared/array-types.js';
import { hunkerUnavailableReason, brokenObjectHunkers, hunkerStatuses } from '../janus-engine/actions/hunker.js';
import { actorEncounterFor, actorRowFor, movementScopeReaders, movementPermission } from './movement-truth.js';
import { heldHunkerObject } from './hunker-object.js';
import { commitLatchVerdict, planObjectHunkerEnds } from './gm-boundary-truth.js';

type Row = Record<string, unknown>;
const upper = (v: unknown) => String(v ?? '').toUpperCase();
export function hunkerObjectReason(encounterId: string, actorId: string, objectId: string): string | null {
  const host = window as HunkerHost;
  if (typeof host.ftTryCommitAbilityUse !== 'function' || !host.ftClaimInteractionMode) return 'Hunker Down is still loading.';
  const encounter = actorEncounterFor(upper(encounterId)) as Row | null, actor = actorRowFor(upper(encounterId), upper(actorId)) as Row | null;
  if (!encounter || !actor) return 'Your creature’s encounter is still loading.';
  if (!movementPermission(encounterId, actorId).allowed) return 'You cannot act for this creature.';
  try { return hunkerUnavailableReason(encounter, actor, heldHunkerObject(encounter, objectId), movementScopeReaders(encounter).value); }
  catch { return 'The object’s physical support is still loading.'; }
}

/** Presentation reads the same persisted object link on every viewer, including late loads. */
export function hunkerObjectForActor(encounterId: string, actorId: string): string {
  const actor = actorRowFor(upper(encounterId), upper(actorId)) as Row | null;
  return actor ? statusCasterId(hunkerStatuses(actor)[0] ?? {}) : '';
}
// Object saves have already settled. Retire only links whose stored object position changed;
// the initiating actor may affect other creatures, just as any other engine action does.
const tails = new Map<string, Promise<void>>();
function settleObjectHunkers(encounterId: string, actorId: string, ids: string[]): void {
  const key = upper(encounterId);
  const prior = tails.get(key) ?? Promise.resolve();
  const next = prior.then(async () => {
    const encounter = actorEncounterFor(key) as Row | null;
    if (!encounter) return;
    const removed = brokenObjectHunkers(encounter, new Set(ids.map(upper)));
    if (!removed.length) return;
    const actingId = actorId || removed.find(status => movementPermission(key, status.target).allowed)?.target || '';
    const actor = actorRowFor(key, actingId);
    if (!actor || !movementPermission(key, actingId).allowed) return;
    const verdict = await planObjectHunkerEnds(key, actingId, ids);
    if (await commitLatchVerdict(key, actingId, verdict, Number(encounterCreatureField(actor.encounter_creatures, 'movement_rev') ?? 0)) !== 'handled')
      window.displayWarning?.('The object moved, but ending Hunker Down could not be saved.');
  }).catch(error => { console.error('Hunker Down object settlement failed', error); window.displayWarning?.('Ending Hunker Down after the object changed could not be saved.'); });
  tails.set(key, next);
  void next.finally(() => { if (tails.get(key) === next) tails.delete(key); });
}
window.addEventListener('ft:inventory-floor-move-truth-committed', event => {
  const c = (event as CustomEvent).detail?.envelope;
  if (c) settleObjectHunkers(c.encounter_id, c.actor_id, [c.inventory_id, ...(c.followers ?? []).map((r: Row) => String(r.id))]);
});
window.addEventListener('ft:inventory-destroy-truth-committed', event => {
  const c = (event as CustomEvent).detail?.command;
  if (c) settleObjectHunkers(c.encounter_id, c.actor_id, [c.inventory_id]);
});
window.addEventListener('ft:inventory-move-receipt-applied', event => {
  const receipt = (event as CustomEvent).detail;
  if (receipt) for (const eid of receipt.affected_encounter_ids ?? [])
    settleObjectHunkers(eid, '', [receipt.move?.source?.inventory_id ?? '']);
});
declare global { interface Window { ftHunkerObjectReason?: typeof hunkerObjectReason } }
window.ftHunkerObjectReason = hunkerObjectReason;
(window as HunkerHost).ftHunkerObjectForActor = hunkerObjectForActor;

window.addEventListener('ft:object-manipulation-receipt', event => {
  const receipt = (event as CustomEvent).detail;
  if (receipt?.reason === null && movementPermission(receipt.encounter_id, receipt.actor.id).allowed)
    settleObjectHunkers(receipt.encounter_id, receipt.actor.id, [receipt.object.inventory_id]);
});

window.addEventListener('ft:hunker-cover-changed', event => {
  const d = (event as CustomEvent).detail;
  if (d) settleObjectHunkers(d.encounterId, d.actorId, d.ids);
});

for (const type of ['ft:inventory-base-compiled', 'ft:encounter-installed', 'ft:encounter-state-received']) window.addEventListener(type, event => {
  const d = (event as CustomEvent).detail;
  if (d?.inventoryUnchanged === true) return;
  const id = upper(d?.encounterId ?? d?.entityId);
  const encounter = actorEncounterFor(id) as Row | null;
  if (!encounter || !Array.isArray(encounter.encounter_inventories_arr) || !inventoryFloorAvailable(encounter)) return;
  const ids = (encounter.encounter_creatures_arr as Row[] ?? []).flatMap(actor => hunkerStatuses(actor).map(statusCasterId));
  if (ids.length) settleObjectHunkers(id, '', ids);
});
